import { get, post, put } from "@/services/api.services";
import { Attribute, RelationOptions, ThingSchema } from "@/services/things/models";
import { SavedObject } from "@/services/commonModels";

export type AttributeAdminConfig = Attribute & {
    subordinateAttribute?: Attribute | null;
};

export type ThingSchemaAdminConfig = ThingSchema & {
    oneTimeAttributeConfigurations: Attribute[];
    timeSeriesAttributeConfigurations: Attribute[];
    templateOneTimeAttributeConfigurations: Attribute[];
    templateTimeSeriesAttributeConfigurations: Attribute[];
};

/**
 * For now, an abstraction of Protocol that contains ThingSchemas, or a container for
 * the collection of template ThingSchemas.
 */
export type AbstractSchemaConfig = {
    code: string;
    /**
     * Exists on Protocol, but not on TemplateSchemaConfig
     */
    name?: string;
    thingSchemas: ThingSchemaAdminConfig[];
};

export type AbstractSchemaConfigWithGlobalRelationOptions = AbstractSchemaConfig & {
    globalRelationOptions: RelationOptions[];
};

export type ThingSchemaMutationData = Omit<
    ThingSchema,
    "id" | "createdAt" | "updatedAt" | "protocolCode" | "schemaConfigId"
>;

export type ThingSchemaTemplate = Omit<
    ThingSchema,
    "id" | "createdAt" | "updatedAt" | "protocolCode" | "schemaConfigId"
>;

export type SavedThingSchemaTemplate = ThingSchemaTemplate & SavedObject<string>;

export type UnsavedSchemaConfig = {
    name: string;
};

export type SavedSchemaConfigDTO = UnsavedSchemaConfig &
    SavedObject<string> & {
        code: string;
    };

export type SavedSchemaConfigSummary = SavedSchemaConfigDTO & {
    numberOfSchemas: number;
    hasThingSchemas: boolean;
};

export type SchemaConfigAdminConfig = SavedSchemaConfigDTO & {
    thingSchemas: ThingSchemaAdminConfig[];
};

export const manageSchemasAPI = {
    fetchTemplateSchemaConfig: async (): Promise<AbstractSchemaConfig> => {
        return get(`/schemas/manage/template`);
    },
    fetchAllSchemaConfigs: async (): Promise<SavedSchemaConfigDTO[]> => {
        return get(`/schemas/schemaConfigs`);
    },
    createSchemaConfig: async (
        unsavedSchemaConfig: UnsavedSchemaConfig
    ): Promise<SavedSchemaConfigDTO> => {
        return post(`/schemas/schemaConfigs`, unsavedSchemaConfig);
    },
    fetchSchemaConfigDTOById: async (id: string): Promise<SavedSchemaConfigDTO> => {
        return get(`/schemas/schemaConfigs/${id}`);
    },
    fetchSchemaConfigsByIds: async (schemaConfigIds: string[]): Promise<AbstractSchemaConfig[]> => {
        const queryString = schemaConfigIds
            .map((schemaConfigId) => {
                return `ids=${schemaConfigId}`;
            })
            .join("&");
        return get(`/schemas/schemaConfigs?${queryString}`);
    },
    updateSchemaConfig: async (
        id: string,
        savedSchemaConfig: SavedSchemaConfigDTO
    ): Promise<SavedSchemaConfigDTO> => {
        return put(`/schemas/schemaConfigs/${id}`, savedSchemaConfig);
    },
    fetchProtocolAdminConfig: async (code: string): Promise<AbstractSchemaConfig> => {
        return get(`/schemas/manage/protocol/${code}`);
    },
    fetchSchemaConfigAdminConfig: async (code: string): Promise<SchemaConfigAdminConfig> => {
        return get(`/schemas/manage/schemaConfigs/${code}`);
    },
    fetchTemplateThingSchemas: async (): Promise<SavedThingSchemaTemplate[]> => {
        return get(`/schemas/manage/templates`);
    },
    updateThingSchema: async (id: string, thingSchemaMutationData: ThingSchemaMutationData) => {
        return post(`/schemas/manage/thingSchemas/${id}`, thingSchemaMutationData);
    },
    addTemplateThingSchemasToProtocol: async (
        protocolCode: string,
        templateThingSchemaIds: string[]
    ) => {
        return put(`/schemas/manage/protocol/${protocolCode}/addThingSchemas`, {
            templateThingSchemaIds,
        });
    },
    addTemplateThingSchemasToSchemaConfig: async (
        schemaConfigId: string,
        templateThingSchemaIds: string[]
    ) => {
        return put(`/schemas/manage/schemaConfigs/${schemaConfigId}/addThingSchemas`, {
            templateThingSchemaIds,
        });
    },

    createBlankThingSchemaTemplate: async (name: string) => {
        return post(`/schemas/manage/templates/${name}`);
    },
};
